import {
  useQueryClient,
} from '@tanstack/react-query'
import {
  createColumnHelper,
  getCoreRowModel,
  getPaginationRowModel,
  type Row,
  type RowSelectionState,
  type SortingState,
  useReactTable,
  type VisibilityState,
} from '@tanstack/react-table'
import {
  CircleCheckBig,
  Edit,
  ExternalLink,
  MessageCircleOff,
} from 'lucide-react'
import React, { useContext, useEffect, useState } from 'react'
import { toast } from 'sonner'

import { Checkbox } from 'components/ui/checkbox'
import { brokersMembers } from 'rails_routes'
import { Tooltip, TooltipProvider } from 'slab'
import { usePatchBrokersBrokerAttentionNeededItems } from 'utils/web_api'
import { type BrokersBrokerAttentionNeededItem } from 'utils/web_api.schemas'

import AssignBrokersToNAVItem from './AssignBrokersToNAVItem'
import AssignTagsToNAVItem from './AssignTagsToNAVItem'
import { defaultVisibilityOverrides } from './NeedsAttentionMemberView'
import { SilenceDialog } from './NeedsAttentionSilence'
import { ReasonCell } from './ReasonCell'
import { StatusCell } from './StatusCell'
import { type ManualReason } from './types'
import { manualNAVItemTypes, setMissingPaymentsQueryDataCallback } from './utils'
import { getLocaleDateString } from '../../../utils'
import NeedsAttentionContext from '../../context/NeedsAttentionContext'
import {
  useGetBrokersMissingPayments,
  type useGetBrokersMissingPaymentsParams,
} from '../../hooks/useGetBrokersMissingPayments'
import { SortableHeader } from '../../reusable/table/SortableHeader'
import TanstackTable, {
  getHeaderCheckboxState,
  type PaginationMetadata,
} from '../../reusable/table/TanstackTable'

export const MAX_NUMBER_OF_SELECTED_ITEMS = 100

const columnHelper = createColumnHelper<BrokersBrokerAttentionNeededItem>()

export const DisplayTextOnHoverTooltip = ({
  text,
  classNames,
}: {
  text: string
  classNames: string
}) => {
  return (
    <Tooltip content={text}>
      <div className={`${classNames} truncate`}>{text}</div>
    </Tooltip>
  )
}

const ResolveOrEditNAVItem = ({
  isResolved,
  isVisible,
  isManual,
  handleAttemptToResolve,
  item
}: {
  item: BrokersBrokerAttentionNeededItem
  isResolved: boolean
  isVisible: boolean
  isManual: boolean
  handleAttemptToResolve: () => void
}) => {
  const { setCreateOrEditNAVItemOptions } =
    useContext(NeedsAttentionContext)

  if (!isVisible) {
    return <div />
  }

  if (isManual) {
    return (
      <Tooltip content='Edit NAV item'>
        <div
          className='flex items-center h-full cursor-pointer'
          onClick={(e) => {
            e.preventDefault()
            setCreateOrEditNAVItemOptions({ isOpen: true, item })
          }}
        >
          <Edit size={20} className='stroke-current text-gray-600' />
        </div>
      </Tooltip>
    )
  }

  if (isResolved) {
    return <div />
  }

  return (
    <Tooltip content='Attempt to resolve'>
      <div
        className='flex items-center h-full cursor-pointer'
        onClick={(e) => {
          e.preventDefault()
          handleAttemptToResolve()
        }}
      >
        <CircleCheckBig size={20} className='stroke-current text-gray-600' />
      </div>
    </Tooltip>
  )
}

const columns = [
  columnHelper.display({
    id: 'select',
    header: ({ table }) => {
      return (
        <div onClick={(e) => e.stopPropagation()} className='flex items-center'>
          <Checkbox
            checked={getHeaderCheckboxState(table)}
            onClick={table.getToggleAllPageRowsSelectedHandler()}
          />
        </div>
      )
    },
    cell: ({ row }) => {
      return (
        <div onClick={(e) => e.stopPropagation()} className='flex items-center'>
          <Checkbox
            checked={row.getIsSelected()}
            disabled={!row.getCanSelect()}
            onClick={row.getToggleSelectedHandler()}
          />
        </div>
      )
    },
  }),
  columnHelper.display({
    header: 'Actions',
    cell: ({ row }: { row: Row<BrokersBrokerAttentionNeededItem> }) => {
      const queryClient = useQueryClient()
      const { getBrokersMissingPaymentsQueryKey, actionsVisibilityOverrides } =
        React.useContext(NeedsAttentionContext)

      const [silenceOpen, setSilenceOpen] = useState(false)
      const modifyNAVItemsMutation = usePatchBrokersBrokerAttentionNeededItems()

      const handleAttemptToResolve = () => {
        modifyNAVItemsMutation.mutate(
          {
            data: {
              broker_attention_needed_item_ids: [row.original.id],
              attempt_to_resolve: true,
            },
          },
          {
            onSuccess: (response) => {
              // Update cached data with new NAV items that include updated tags.
              queryClient.setQueryData(
                getBrokersMissingPaymentsQueryKey,
                setMissingPaymentsQueryDataCallback(response)
              )
              toast.success('Attempted to resolve NAV item!', {
                position: 'top-center',
              })
            },
          }
        )
      }

      const {
        silence: silenceVisible,
        resolve: resolveVisible,
        openInNewTab: openInNewTabVisible,
      } = actionsVisibilityOverrides || defaultVisibilityOverrides

      return (
        <TooltipProvider>
          <div className='flex items-center gap-4'>
            {openInNewTabVisible && (
              <Tooltip content='Open in a new tab'>
                <div
                  className='cursor-pointer h-full'
                  onClick={(e) => {
                    e.preventDefault()
                    window.open(
                      brokersMembers.transactions({ member_id: row.original.member_id }),
                      '_blank',
                      'noopener noreferrer'
                    )
                  }}
                >
                  <ExternalLink
                    size={20}
                    className='stroke-current text-gray-600'
                  />
                </div>
              </Tooltip>
            )}
            {row.original.status !== 'resolved' && silenceVisible && (
              <SilenceDialog
                missingPaymentId={row.original.id}
                open={silenceOpen}
                setOpen={setSilenceOpen}
              >
                <Tooltip content='Silence'>
                  <div
                    className='flex items-center h-full cursor-pointer'
                    onClick={(e) => {
                      e.preventDefault()
                      setSilenceOpen(true)
                    }}
                  >
                    <MessageCircleOff
                      size={20}
                      className='stroke-current text-gray-600'
                    />
                  </div>
                </Tooltip>
              </SilenceDialog>
            )}
            <ResolveOrEditNAVItem
              isResolved={row.original.status === 'resolved'}
              isVisible={resolveVisible}
              isManual={manualNAVItemTypes.includes(
                row.original.reason as ManualReason
              )}
              handleAttemptToResolve={handleAttemptToResolve}
              item={row.original}
            />
          </div>
        </TooltipProvider>
      )
    },
  }),
  columnHelper.accessor('created_at', {
    header: (info) => (
      <SortableHeader<BrokersBrokerAttentionNeededItem> {...info} headerText='Date' />
    ),
    cell: (info) => getLocaleDateString(new Date(info.getValue())),
    enableSorting: true,
  }),
  columnHelper.accessor('status', {
    header: (info) => (
      <SortableHeader<BrokersBrokerAttentionNeededItem> {...info} headerText='Status' />
    ),
    cell: (info) => <StatusCell value={info.getValue()} />,
    enableSorting: true,
  }),
  columnHelper.accessor('tags', {
    header: 'Tags',
    cell: ({ getValue, row }) => {
      const { id } = row.original
      return (
        <AssignTagsToNAVItem navItemId={id} assignedTags={getValue() || []} />
      )
    },
  }),
  columnHelper.accessor('brokers', {
    header: 'Assignees',
    cell: ({ getValue, row }) => {
      const { id } = row.original
      return (
        <AssignBrokersToNAVItem
          navItemId={id}
          assignedBrokers={getValue() || []}
        />
      )
    },
  }),
  columnHelper.accessor('reason', {
    header: (info) => (
      <SortableHeader<BrokersBrokerAttentionNeededItem> {...info} headerText='Reason' />
    ),
    cell: (info) => <ReasonCell value={info.getValue()} />,
    enableSorting: true,
  }),
  columnHelper.accessor('amount_paid', {
    header: 'Amount Paid',
    cell: (info) => `$${info.getValue().toFixed(2)}`,
  }),
  columnHelper.accessor(
    (row) => `${row.employee_first_name} ${row.employee_last_name}`,
    {
      header: 'Employee',
      id: 'employee',
      cell: (info) => (
        <DisplayTextOnHoverTooltip
          text={info.getValue() || 'N/A'}
          classNames='max-w-40'
        />
      ),
    }
  ),
  columnHelper.accessor('enrollment_type', {
    header: 'Enrollment Type',
    cell: (info) => <span className='capitalize'>{info.getValue()}</span>,
  }),
  columnHelper.accessor('employer_name', {
    header: 'Employer',
    cell: (info) => (
      <DisplayTextOnHoverTooltip
        text={info.getValue() || 'N/A'}
        classNames='max-w-40'
      />
    ),
  }),
  columnHelper.accessor('carrier_name', {
    header: 'Carrier',
    cell: (info) => (
      <DisplayTextOnHoverTooltip
        text={info.getValue() || 'N/A'}
        classNames='max-w-40'
      />
    ),
  }),
  columnHelper.accessor('brokerage_name', {
    header: 'Brokerage',
    cell: (info) => (
      <DisplayTextOnHoverTooltip
        text={info.getValue() || 'N/A'}
        classNames='max-w-40'
      />
    ),
  }),
  columnHelper.accessor('submission_method', {
    header: 'Submission Method',
  }),
  columnHelper.accessor('category', {
    header: 'Category',
  }),
  columnHelper.accessor('state', {
    header: 'State',
  }),
  columnHelper.accessor('amount_expected', {
    header: 'Amount Expected',
    cell: (info) => {
      const value = info.getValue()
      return value.map((val) => `$${val.toFixed(2)}`).join(', ')
    },
  }),
  columnHelper.accessor('notes', {
    header: 'Notes',
    cell: (info) => (
      <DisplayTextOnHoverTooltip
        text={info.getValue() || 'N/A'}
        classNames='max-w-60'
      />
    ),
  }),
  columnHelper.accessor('silenced_by_name', {
    header: 'Silenced By',
    cell: (info) => {
      const status = info.row.original.status
      const silencedBy = info.getValue()
      return status === 'silenced' ? silencedBy : null
    },
  }),
]

export const defaultFilters = {
  before_date: '',
  after_date: '',
  reimbursement_only: '',
  employer_ids: { include: [], exclude: [] },
  member_ids: { include: [], exclude: [] },
  enrollment_types: { include: [], exclude: [] },
  statuses: { include: [], exclude: [] },
  reasons: { include: [], exclude: [] },
  carrier_names: { include: [], exclude: [] },
  submission_methods: { include: [], exclude: [] },
  brokerage_ids: { include: [], exclude: [] },
  plan_years: { include: [], exclude: [] },
  enrollment_categories: { include: [], exclude: [] },
  enrollment_states: { include: [], exclude: [] },
  employment_statuses: { include: [], exclude: [] },
  tag_ids: { include: [], exclude: [] },
  broker_ids: { include: [], exclude: [] },
}

// These are settings saved in local storage so users can configure their own views.
export type NeedsAttentionSettings = {
  pageSize: string
}

export type NeedsAttentionSorting = {
  state?: 'asc' | 'desc'
  created_at?: 'asc' | 'desc'
}

export type NeedsAttentionPagination = {
  pageIndex: number
  pageSize: number
}

export type NeedsAttentionFilter = {
  created_at?: string
  state?: 'active' | 'silenced' | 'resolved'
  employer_name?: string
  member_name?: string
  enrollment_type?: 'medical' | 'dental' | 'vision'
  reason?:
    | 'missing_payment'
    | 'under_payment'
    | 'delinquency_risk'
    | 'over_payment'
    | 'unexpected_amount'
    | 'automation_reconciliation'
}

export type NeedsAttentionTableProps = {
  missingPaymentsArgs: useGetBrokersMissingPaymentsParams
  rowSelection: RowSelectionState
  setRowSelection: React.Dispatch<React.SetStateAction<RowSelectionState>>
  setPagination: React.Dispatch<React.SetStateAction<NeedsAttentionPagination>>
  setSorting: React.Dispatch<React.SetStateAction<SortingState>>
  setSettings: React.Dispatch<React.SetStateAction<NeedsAttentionSettings>>
  columnVisibilityOverrides?: VisibilityState
}

export function NeedsAttentionTableImpl ({
  missingPaymentsArgs,
  rowSelection,
  setRowSelection,
  setSorting,
  setPagination,
  columnVisibilityOverrides = {},
}: NeedsAttentionTableProps) {
  const [paginationMetadata, setPaginationMetadata] = useState<
    PaginationMetadata | undefined
  >()

  const {
    data: missingPaymentsData,
    isLoading,
    isError,
  } = useGetBrokersMissingPayments(missingPaymentsArgs)

  const { sorting, pagination } = missingPaymentsArgs

  const table = useReactTable({
    data: missingPaymentsData?.data || [],
    columns,
    manualPagination: true,
    manualFiltering: true,
    manualSorting: true,
    getCoreRowModel: getCoreRowModel(),
    getPaginationRowModel: getPaginationRowModel(),
    getRowId: (row) => row.id,
    enableRowSelection: (row) => {
      return (
        Object.keys(rowSelection).length < MAX_NUMBER_OF_SELECTED_ITEMS ||
        rowSelection[row.id] === true
      )
    },
    onRowSelectionChange: (updater) => {
      setRowSelection((prev) => {
        const newSelection =
          typeof updater === 'function' ? updater(prev) : updater
        const selectedRowIds = Object.keys(newSelection)
        if (selectedRowIds.length > MAX_NUMBER_OF_SELECTED_ITEMS) {
          return prev // Keep the previous selection if over the limit
        }
        return newSelection
      })
    },
    state: {
      sorting,
      pagination,
      rowSelection,
      columnVisibility: columnVisibilityOverrides,
    },
  })

  useEffect(() => {
    if (missingPaymentsData) {
      setPaginationMetadata(missingPaymentsData.pagination)
    }
  }, [missingPaymentsData?.pagination])

  return (
    <TanstackTable
      table={table}
      isLoading={isLoading}
      isError={isError}
      noDataMessage='There are no records that need attention.'
      setSorting={setSorting}
      defaultSortingState={[]}
      paginationLabelSingular='record'
      paginationMetadata={paginationMetadata}
      onNextPageClick={() =>
        setPagination({
          ...pagination,
          pageIndex: paginationMetadata?.next_page || 0,
        })}
      onPreviousPageClick={() =>
        setPagination({
          ...pagination,
          pageIndex: paginationMetadata?.prev_page || 0,
        })}
    />
  )
}

const NeedsAttentionTable = (props: NeedsAttentionTableProps) => {
  return <NeedsAttentionTableImpl {...props} />
}

export default NeedsAttentionTable
